import React, { useEffect, useCallback, useRef, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Divider,
  List,
  ListItem,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Modal,
  Fab,
  Badge,
} from "@mui/material";
import {
  Add,
  Remove,
  Search,
  Delete,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowUp,
  KeyboardArrowDown,
  ShoppingCart,
  Close,
} from "@mui/icons-material";
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from "../../App";
import photo from "../../assets/photo.png";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { getAllProducts } from "../../service/productService";
import { getAllSubCategory, getAllCategoryLists } from "../../service/categoryService";
import Color from "../../components/Color";
import AddProduct from "../Products/AddProduct";
import {
  setProducts,
  setFiltered,
  setSearchTerm,
  setSubCategoryId,
  setSubCategories,
  setVisibleCount,
  setScrollLoading,
  setHasMore,
  setIsAtStart,
  setIsAtEnd,
  setShowScrollUp,
  setShowScrollDown,
  setOpenModal,
  setOpenCartModal,
  setLoading,
  addProduct,
} from '../../store/cakeProductSlice';
import {
  setCartItems,
  addToCart,
  updateQuantity,
  updateManualQuantity,
  removeItem,
} from '../../store/cakeCartSlice';

const CakeOrder = () => {
  const dispatch = useDispatch();
  const {
    products,
    filtered,
    searchTerm,
    subCategoryId,
    subCategories,
    visibleCount,
    scrollLoading,
    hasMore,
    isAtStart,
    isAtEnd,
    showScrollUp,
    showScrollDown,
    openModal,
    openCartModal,
    loading,
  } = useSelector((state) => state.cakeProduct);
  const { cartItems } = useSelector((state) => state.cakeCart);
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const scrollRef = useRef(null);
  const subCategoryRef = useRef(null);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [cakeCategory, setCakeCategory] = useState(null);

  // Fetch categories to find "Cake" category
  const fetchCategories = useCallback(async () => {
    try {
      const response = await getAllCategoryLists();
      if (response.data.status === "success" && Array.isArray(response.data.data)) {
        const cakeCat = response.data.data.find(
          (cat) => cat.name.toLowerCase() === "cake"
        );
        if (cakeCat) {
          setCakeCategory({ id: cakeCat.id, name: "Cake" });
        } else {
          showSnackbar("Cake category not found", "error");
        }
      } else {
        showSnackbar("Failed to load categories", "error");
      }
    } catch (error) {
      showSnackbar("Failed to load category data", "error");
    }
  }, [showSnackbar]);

  // Fetch products
  const fetchAllProducts = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      let all = [],
        page = 1,
        last = 1;
      do {
        const res = await getAllProducts(page);
        const data = res.data.data;
        if (res.data.status !== "success" || !Array.isArray(data.data)) {
          showSnackbar("Unexpected response from server", "error");
          break;
        }
        const cakeProducts = data.data.filter((p) => p.category?.name === "Cake");
        all = all.concat(cakeProducts);
        last = data.last_page;
        page++;
      } while (page <= last);
      dispatch(setProducts(all));
    } catch (err) {
      showSnackbar("Failed to fetch products", "error");
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, showSnackbar]);

  // Fetch subcategories
  const fetchSubcategories = useCallback(async () => {
    if (!cakeCategory) return;
    try {
      const response = await getAllSubCategory({ category_id: cakeCategory.id });
      if (response.data.status === "success" && Array.isArray(response.data.data)) {
        dispatch(setSubCategories(response.data.data));
      } else {
        showSnackbar("Failed to load subcategories", "error");
      }
    } catch (error) {
      showSnackbar("Failed to load subcategory data", "error");
    }
  }, [dispatch, showSnackbar, cakeCategory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (cakeCategory) {
      fetchAllProducts();
      fetchSubcategories();
    }
  }, [fetchAllProducts, fetchSubcategories, cakeCategory]);

  // Compute subcategory counts
  const subcategoryCounts = products.reduce((acc, p) => {
    const subCatId = p.sub_category_id || "Uncategorized";
    const subCat = subCategories.find((sc) => sc.id === p.sub_category_id);
    const subCatName = subCat ? subCat.name : "Uncategorized";
    acc[subCatId] = {
      id: subCatId,
      name: subCatName,
      count: (acc[subCatId]?.count || 0) + 1,
    };
    return acc;
  }, {});

  const uniqueSubcategories = [
    { id: "All", name: "All", count: products.length },
    ...Object.values(subcategoryCounts).map(({ id, name, count }) => ({
      id,
      name,
      count,
    })),
  ];

  // Filtering logic for Cake category and sub_category_id
  useEffect(() => {
    let filteredList = products.filter((p) => p.category?.name === "Cake");
    if (searchTerm) {
      filteredList = filteredList.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (subCategoryId !== "All") {
      filteredList = filteredList.filter(
        (p) => p.sub_category_id === parseInt(subCategoryId)
      );
    }
    dispatch(setFiltered(filteredList));
  }, [dispatch, searchTerm, subCategoryId, products]);

  // Load cart from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("selectedCakes");
    if (stored) dispatch(setCartItems(JSON.parse(stored)));
  }, [dispatch]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || !hasMore) return;
    if (
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 50
    ) {
      dispatch(setScrollLoading(true));
      setTimeout(() => {
        dispatch(setVisibleCount((prev) => {
          const newCount = prev + 20;
          if (newCount >= filtered.length) dispatch(setHasMore(false));
          return newCount;
        }));
        dispatch(setScrollLoading(false));
      }, 500);
    }
  }, [dispatch, hasMore, filtered.length]);

  // Scroll button visibility
  const updateScrollButtons = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    dispatch(setShowScrollUp(container.scrollTop > 50));
    dispatch(
      setShowScrollDown(
        container.scrollTop < container.scrollHeight - container.clientHeight - 50
      )
    );
  }, [dispatch]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleFullScroll = () => {
      handleScroll();
      updateScrollButtons();
    };
    container.addEventListener("scroll", handleFullScroll);
    updateScrollButtons();
    return () => container.removeEventListener("scroll", handleFullScroll);
  }, [handleScroll, updateScrollButtons]);

  const handleScrollButtonClick = useCallback(
    (direction) => {
      const container = scrollRef.current;
      if (!container) return;
      const scrollAmount = direction === "up" ? -300 : 300;
      container.scrollBy({ top: scrollAmount, behavior: "smooth" });
    },
    []
  );

  const handleAddNewCake = (newProduct) => {
    const newId = Math.max(...products.map((p) => p.id), 0) + 1;
    const addedProduct = {
      ...newProduct,
      id: newId,
      category: { name: "Cake", id: cakeCategory ? cakeCategory.id : 167 },
      sub_category_id: parseInt(newProduct.sub_category_id) || 1,
    };
    dispatch(addProduct(addedProduct));
    showSnackbar("New cake added successfully!", "success");
  };

  const handleCategoryScroll = useCallback(
    (direction) => {
      const container = subCategoryRef.current;
      if (!container) return;
      const scrollAmount = direction === "left" ? -150 : 150;
      const maxScroll = container.scrollWidth - container.clientWidth;

      if (direction === "right" && container.scrollLeft >= maxScroll - 1) {
        container.scrollTo({ left: 0, behavior: "smooth" });
        dispatch(setIsAtStart(true));
        dispatch(setIsAtEnd(false));
      } else if (direction === "left" && container.scrollLeft <= 0) {
        container.scrollTo({ left: maxScroll, behavior: "smooth" });
        dispatch(setIsAtStart(false));
        dispatch(setIsAtEnd(true));
      } else {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        setTimeout(() => {
          const newScrollLeft = container.scrollLeft;
          dispatch(setIsAtStart(newScrollLeft <= 0));
          dispatch(setIsAtEnd(newScrollLeft >= maxScroll - 1));
        }, 300);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const container = subCategoryRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      dispatch(setIsAtStart(container.scrollLeft <= 0));
      dispatch(setIsAtEnd(container.scrollLeft >= maxScroll - 1));
    };

    container.addEventListener("scroll", updateScrollState);
    updateScrollState();
    return () => container.removeEventListener("scroll", updateScrollState);
  }, [dispatch, uniqueSubcategories.length]);

  // Compute totals
  const totals = cartItems.reduce(
    (acc, item) => {
      const price = parseFloat(item.base_price);
      const gstRate = parseFloat(item.tax_rate);
      const gstAmtPerItem = (price * gstRate) / 100;
      const totalPerItem = price + gstAmtPerItem;
      acc.gst += gstAmtPerItem * item.quantity;
      acc.total += totalPerItem * item.quantity;
      return acc;
    },
    { total: 0, gst: 0 }
  );

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Render Cart Details
  const renderCartDetails = () => (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        width: "100%",
        bgcolor: "white",
        borderRadius: 2,
        maxHeight: isMobileOrTablet ? "80vh" : "none",
        height: isMobileOrTablet ? "auto" : "765px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "white",
          p: { xs: 1, sm: 2 },
          borderBottom: "1px solid #eee",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            fontSize={{ xs: "16px", sm: "18px" }}
            id="cart-modal-title"
          >
            Order Details
          </Typography>
          {isMobileOrTablet && (
            <IconButton
              onClick={() => dispatch(setOpenCartModal(false))}
              aria-label="Close cart modal"
            >
              <Close />
            </IconButton>
          )}
        </Box>
      </Box>
      <List
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: { xs: 1, sm: 2 },
        }}
      >
        {cartItems.map((item) => {
          const price = parseFloat(item.base_price);
          const gst = parseFloat(item.tax_rate);
          const unitTotal = price + (price * gst) / 100;
          const total = unitTotal * item.quantity;
          return (
            <ListItem
              key={item.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.5,
                width: "100%",
                borderBottom: "1px solid #eee",
                pb: 1,
                px: { xs: 0.5, sm: 0 },
              }}
            >
              <Box display="flex" alignItems="center" width="100%" gap={1}>
                <Box
                  component="img"
                  src={item.image_url || photo}
                  alt={item.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = photo;
                  }}
                  sx={{
                    width: { xs: 40, sm: 50 },
                    height: { xs: 40, sm: 50 },
                    borderRadius: 1,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "12px", sm: "14px" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "10px", sm: "11px" } }}
                  >
                    ₹{unitTotal.toFixed(2)} × {item.quantity}
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                mt={1}
                flexWrap="wrap"
                gap={1}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => dispatch(updateQuantity({ id: item.id, change: -1 }))}
                    disabled={item.quantity <= 1}
                    aria-label={`Decrease quantity of ${item.name}`}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                  <TextField
                    value={item.quantity}
                    onChange={(e) =>
                      dispatch(updateManualQuantity({ id: item.id, value: e.target.value }))
                    }
                    type="number"
                    size="small"
                    inputProps={{
                      min: 1,
                      style: { width: "40px", textAlign: "center", fontSize: "12px" },
                      inputMode: "numeric",
                      "aria-label": `Quantity of ${item.name}`,
                    }}
                    sx={{
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      "& input[type=number]": { MozAppearance: "textfield" },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => dispatch(updateQuantity({ id: item.id, change: 1 }))}
                    aria-label={`Increase quantity of ${item.name}`}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => dispatch(removeItem(item.id))}
                    aria-label={`Remove ${item.name} from cart`}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  <Typography fontSize={{ xs: "12px", sm: "13px" }} fontWeight="bold">
                    ₹{total.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          );
        })}
        {cartItems.length === 0 && (
          <Typography
            variant="body2"
            color="error"
            textAlign="center"
            mt={2}
            fontSize={{ xs: "12px", sm: "14px" }}
          >
            No cakes in cart.
          </Typography>
        )}
      </List>
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          bgcolor: "white",
          p: { xs: 1, sm: 2 },
          borderTop: "1px solid #eee",
        }}
      >
        <Divider sx={{ mb: 2 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: "12px", sm: "14px" } }}
        >
          Total:
        </Typography>
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ fontSize: { xs: "16px", sm: "18px" } }}
        >
          ₹{totals.total.toFixed(2)}
        </Typography>
        <Button
          fullWidth
          sx={{
            mt: 1,
            background: Color.primary.main,
            color: "white",
            fontSize: { xs: "12px", sm: "14px" },
            py: 1,
            "&:disabled": {
              background: "#7e7a7aff",
              color: "#f3f0f0ff",
              cursor: "not-allowed",
              pointerEvents: "auto",
            },
          }}
          onClick={() => {
            dispatch(setOpenCartModal(false));
            localStorage.setItem("categoryId", cakeCategory ? cakeCategory.id : "167");
            navigate("/cake-details");
          }}
          disabled={cartItems.length === 0}
        >
          Proceed Order
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      p={{ xs: 0, sm: 0 }}
      mt={{ xs: 2, md: 0 }}
      mb={{ xs: 0, sm: "auto" }}
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      gap={{ xs: 1, sm: 2 }}
      sx={{ fontFamily: '"Poppins", sans-serif' }}
    >
      <Box
        flex={1}
        sx={{ width: { xs: "100%", md: "70%" }, position: "relative" }}
      >
        <Box
          sx={{
            position: "sticky",
            top: { xs: 0, md: 17 },
            zIndex: 10,
            backgroundColor: "white",
            borderRadius: "6px",
            width: '99%'
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={{ xs: 1, sm: 2 }}
            alignItems="center"
            sx={{ width: "100%" }}
            p={{ xs: 1, md: 2 }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search cakes..."
              value={searchTerm}
              onChange={(e) => dispatch(setSearchTerm(e.target.value))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{
                flex: 1,
                width: { xs: "100%", sm: "250px" },
                borderRadius: "6px",
              }}
            />
            <Button
              sx={{
                flexShrink: 0,
                minWidth: { xs: "100%", sm: "150px", md: "180px" },
                background: Color.primary.main,
                color: "white",
                fontSize: { xs: "12px", sm: "14px" },
              }}
              startIcon={<AddIcon />}
              onClick={() => dispatch(setOpenModal(true))}
            >
              Add Product
            </Button>
          </Box>
          <Paper
            sx={{
              height: { xs: 48, sm: 56, md: 60 },
              mt: { xs: 1, sm: 0 },
              mb: { xs: 1, sm: 4 },
              px: { xs: 1, sm: 1.5 },
              backgroundColor: "#f9f9f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              overflow: "hidden",
              borderRadius: 2,
            }}
            elevation={2}
          >
            <IconButton
              onClick={() => handleCategoryScroll("left")}
              disabled={isAtStart || uniqueSubcategories.length <= 1}
              sx={{
                flexShrink: 0,
                p: { xs: 0.5, sm: 1 },
                color: Color.primary.main,
                "&:disabled": { color: "#ccc" },
                border: "1px solid gray",
              }}
              aria-label="Scroll subcategories left"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Box
              ref={subCategoryRef}
              sx={{
                display: "flex",
                gap: { xs: 0.8, sm: 1.2, md: 1.2 },
                pl: 1,
                overflowX: "auto",
                whiteSpace: "nowrap",
                flexGrow: 1,
                py: 0.5,
                scrollBehavior: "smooth",
                boxShadow: "none",
                "&::-webkit-scrollbar": { display: "none" },
                "-ms-overflow-style": "none",
                "scrollbar-width": "none",
              }}
            >
              {uniqueSubcategories.map((subCat) => (
                <Button
                  key={subCat.id}
                  variant={subCategoryId === subCat.id ? "contained" : "outlined"}
                  onClick={() => dispatch(setSubCategoryId(subCat.id))}
                  sx={{
                    flexShrink: 0,
                    borderRadius: "20px",
                    textTransform: "none",
                    px: { xs: 0.8, sm: 1, md: 1.2 },
                    py: { xs: 0.4, sm: 0.5 },
                    fontSize: { xs: "10px", sm: "11px", md: "12px" },
                    fontWeight: 500,
                    minWidth: { xs: "auto", sm: "70px", md: "80px" },
                    background: subCategoryId === subCat.id ? Color.primary.main : "white",
                    color: subCategoryId === subCat.id ? "white" : "black",
                    borderColor: Color.primary.main,
                    boxShadow: "none",
                    "&:hover": {
                      background:
                        subCategoryId === subCat.id ? Color.primary.dark : "#f1f1f1",
                    },
                  }}
                >
                  {`${subCat.name} (${subCat.count})`}
                </Button>
              ))}
            </Box>
            <IconButton
              onClick={() => handleCategoryScroll("right")}
              disabled={isAtEnd || uniqueSubcategories.length <= 1}
              sx={{
                flexShrink: 0,
                p: { xs: 0.5, sm: 1 },
                color: Color.primary.main,
                "&:disabled": { color: "#ccc" },
                border: "1px solid gray",
              }}
              aria-label="Scroll subcategories right"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Paper>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              ref={scrollRef}
              sx={{
                height: { xs: "60vh", sm: "70vh", md: "80vh" },
                overflowY: "auto",
                pr: { xs: 0.5, sm: 1 },
              }}
            >
              <Grid
                container
                spacing={{ xs: 1, sm: 2, md: 2 }}
                justifyContent="flex-start"
                sx={{
                  display: { xs: "grid", sm: "flex" },
                  gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                  gap: { xs: "8px", sm: 2, md: 2 },
                }}
              >
                {filtered.slice(0, visibleCount).map((p) => {
                  const price = parseFloat(p.base_price);
                  const gst = parseFloat(p.tax_rate);
                  const total = price + (price * gst) / 100;
                  return (
                    <Grid
                      item
                      xs={6}
                      sm={6}
                      md={4}
                      lg={3}
                      key={p.id}
                      display="flex"
                      justifyContent="flex-start"
                      mt={2}
                    >
                      <Card
                        role="button"
                        aria-label={`Add ${p.name} to cart`}
                        onClick={() => dispatch(addToCart(p))}
                        sx={{
                          width: { xs: '100%', sm: 150.7 ,md: 148, lg:181.7  },
                          height: { xs: 'auto', sm: 220, md: 225, lg:230},
                          borderRadius: 2,
                          boxShadow: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          p: 1,
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={p.image_url || photo}
                          alt={p.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = photo;
                          }}
                          style={{
                            width: "100%",
                            height: { xs: "100%", md: "70%" },
                            borderRadius: 10,
                            objectFit: "cover",
                            marginBottom: 0,
                            aspectRatio: { xs: "1/1", sm: "auto" },
                          }}
                        />
                        <CardContent
                          sx={{
                            pl: 0.5,
                            overflow: "hidden",
                            whiteSpace: "normal",
                            textOverflow: "ellipsis",
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={400}
                            sx={{
                              fontSize: { xs: "11px", sm: "12px", md: "12px", lg:'13px' },
                              lineHeight: 1,
                              color: "black",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              wordBreak: "break-word",
                            }}
                            title={p.name}
                          >
                            {p.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: { xs: "10px", sm: "12px" } }}
                          >
                            ₹{total.toFixed(2)} <span style={{ color: "gray" }}>per Kg</span>
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {scrollLoading && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!hasMore && !scrollLoading && (
                <Typography textAlign="center" py={2} color="text.secondary" fontWeight={700}>
                  No more cakes to load
                </Typography>
              )}
            </Box>
            {isMobileOrTablet && (
              <Box
                sx={{
                  position: "fixed",
                  bottom: 150,
                  right: 15,
                  zIndex: 5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <IconButton
                  onClick={() => handleScrollButtonClick("up")}
                  disabled={!showScrollUp}
                  sx={{
                    bgcolor: "white",
                    boxShadow: 2,
                    color: Color.primary.main,
                    "&:hover": { bgcolor: "grey.100" },
                    "&:disabled": { bgcolor: "grey.200", color: "grey.500" },
                  }}
                  size="small"
                  aria-label="Scroll up"
                >
                  <KeyboardArrowUp />
                </IconButton>
                <IconButton
                  onClick={() => handleScrollButtonClick("down")}
                  disabled={!showScrollDown}
                  sx={{
                    bgcolor: "white",
                    boxShadow: 2,
                    color: Color.primary.main,
                    "&:hover": { bgcolor: "grey.100" },
                    "&:disabled": { bgcolor: "grey.200", color: "grey.500" },
                  }}
                  size="small"
                  aria-label="Scroll down"
                >
                  <KeyboardArrowDown />
                </IconButton>
              </Box>
            )}
            {isMobileOrTablet && (
              <Fab
                color="primary"
                onClick={() => dispatch(setOpenCartModal(true))}
                sx={{
                  position: "fixed",
                  bottom: 80,
                  right: 20,
                  zIndex: 10,
                  background: Color.primary.main,
                  "&:hover": {
                    background: Color.primary.main,
                  },
                  "&:active": {
                    background: Color.primary.main,
                  },
                  "&:focus": {
                    background: Color.primary.main,
                  },
                }}
                aria-label={`View cart with ${totalItems} items`}
              >
                <Badge badgeContent={totalItems} color="secondary" size="medium" width={'40px'}>
                  <ShoppingCart />
                </Badge>
              </Fab>
            )}
          </>
        )}
      </Box>
      {isMobileOrTablet && (
        <Modal
          open={openCartModal}
          onClose={() => dispatch(setOpenCartModal(false))}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          aria-labelledby="cart-modal-title"
        >
          <Box
            sx={{
              width: { xs: "90%", sm: "80%" },
              maxWidth: 400,
              bgcolor: "white",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {renderCartDetails()}
          </Box>
        </Modal>
      )}
      {!isMobileOrTablet && (
        <Box
          width={{ xs: "100%", sm: "100%", md: "25%", lg: "25%" }}
          minWidth={{ xs: "auto", md: "300px" }}
          height="765px"
          sx={{
            position: { md: "sticky", xs: "static" },
            top: { md: 15, lg:17 },
          }}
        >
          {renderCartDetails()}
        </Box>
      )}
      <AddProduct
        open={openModal}
        onClose={() => dispatch(setOpenModal(false))}
        onAdd={handleAddNewCake}
        subCategories={subCategories}
        defaultCategory={cakeCategory}
      />
    </Box>
  );
};

export default CakeOrder;
